import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { Accommodation, PerformerRequest } from '@silbo/core';
import { colors, Emoji, palette } from '@silbo/ui';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatAge } from '../../../utils/format-age';
import { getPatientEmojiIcon } from '../../../utils/get-patient-emoji-icon';
import { formatRequestDate } from '../utils/format-request-date';
import { getRequestReason } from '../utils/get-request-reason';
import RequestStatusChip from './RequestStatusChip';
import SpecificityChip from './SpecificityChip';

type RequestDetailsContentProps = {
  request: PerformerRequest;
  accommodation?: Accommodation;
};

const RequestDetailsContent = ({ request, accommodation }: RequestDetailsContentProps) => {
  const { t } = useTranslation('bed-management');

  if (!request.request.visit) {
    return null;
  }

  const visit = request.request.visit;
  const reason = getRequestReason(request);

  return (
    <Stack spacing={4}>
      <Box
        sx={{
          backgroundColor: palette.primary.main,
          borderRadius: 1,
          padding: 3,
          color: 'white',
        }}
      >
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <Emoji name={getPatientEmojiIcon(visit.gender, visit.age)} bgColor="white" />
          <Typography fontWeight={600}>{visit.fullName}</Typography>
          <Chip
            sx={{
              backgroundColor: colors.blue[50],
            }}
            color="default"
            variant="light"
            label={
              <Typography variant="body2" color="white">
                {formatAge(visit.birthdate)} ({format(visit.birthdate, 'dd/MM/yyyy')})
              </Typography>
            }
          />
        </Stack>
        <Stack direction="row" spacing={4}>
          <Box>
            <Typography variant="body2">{t('requestDetails.patientCard.pan')} :</Typography>
            <Typography variant="body2" fontWeight={600}>
              {visit.pan}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">{t('requestDetails.patientCard.ipp')} :</Typography>
            <Typography variant="body2" fontWeight={600}>
              {visit.ipp}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Stack spacing={4} divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" gap={2}>
            <RequestStatusChip status={request.status} />
            <Typography variant="body2" color="secondary">
              {formatRequestDate(request)}
            </Typography>
          </Stack>
          {request.status === 'REJECTED' && (
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="body2" color="secondary">
                {t('requestDetails.rejectionReason')}
              </Typography>
              <Typography>{request.rejectionReason || '-'}</Typography>
            </Stack>
          )}
        </Stack>

        {request.status === 'ACCEPTED' && accommodation && (
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" color="secondary" minWidth={30}>
              {t('requestDetails.the')}
            </Typography>
            <Typography fontWeight={600} minWidth={30}>
              {format(accommodation.startAt, 'dd/MM/yyyy')}
              <Typography component="span" variant="body2" color="secondary">
                {` ${t('requestDetails.at').toLowerCase()} `}
              </Typography>
              {format(accommodation.startAt, 'HH:mm')}
            </Typography>
          </Stack>
        )}

        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" color="secondary" minWidth={30}>
              {t('requestDetails.from')}
            </Typography>
            <Typography fontWeight={600} minWidth={30}>
              {request.request.originAccommodation?.unit?.name || request.request.directAdmissionOrigin || '-'}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" color="secondary">
              {t('requestDetails.to')}
            </Typography>
            <Typography fontWeight={600}>{request.performer.name}</Typography>
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" color="secondary">
            {t('requestDetails.movementType')}
          </Typography>
          <Typography>
            {request.request.requestType === 'INTERNAL_MUTATION' ? 'Mutation interne' : 'Admission directe'}
          </Typography>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" color="secondary">
            {t('requestDetails.admitReason')}
          </Typography>
          <Typography>{reason || '-'}</Typography>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" color="secondary">
            {t('requestDetails.responsibleUnit')}
          </Typography>
          <Typography>{request.request.originAccommodation?.responsibleUnit?.name || '-'}</Typography>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" color="secondary">
            {t('requestDetails.customSpecificities')}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {request.request.customSpecificities?.length ? (
              request.request.customSpecificities.map((spec) => (
                <SpecificityChip key={spec.id} specificity={spec} size="small" />
              ))
            ) : (
              <Typography>-</Typography>
            )}
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" color="secondary">
            {t('requestDetails.comment')}
          </Typography>
          <Typography>{request.request.comment || '-'}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RequestDetailsContent;
